import { makeStyles } from '@material-ui/core/styles';

// Logo-based color palette
export const colors = {
  primary: '#61DAFB',      // Light blue from logo
  secondary: '#FFD700',    // Gold accent
  tertiary: '#4A90E2',     // Dark blue
  dark: '#2C3E50',         // Dark gray
  light: '#E8F4FD',        // Light blue background
  white: '#FFFFFF',
  text: '#34495E',
  textLight: '#7F8C8D',
};

export const useSharedStyles = makeStyles((theme) => ({
  // Hero Section Styles
  heroSection: {
    position: 'relative',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, rgba(97, 218, 251, 0.4) 0%, rgba(74, 144, 226, 0.3) 50%, rgba(44, 62, 80, 0.2) 100%)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `rgba(44, 62, 80, 0.3)`,
    zIndex: 1,
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    color: colors.white,
    maxWidth: 800,
    padding: '0 20px',
  },
  heroTitle: {
    fontSize: '3.5rem',
    fontWeight: 700,
    marginBottom: '1rem',
    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  heroSubtitle: {
    fontSize: '1.5rem',
    fontWeight: 300,
    marginBottom: '2rem',
    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.2rem',
    },
  },
  heroButton: {
    background: `linear-gradient(45deg, ${colors.primary} 30%, ${colors.secondary} 90%)`,
    border: 0,
    borderRadius: 25,
    boxShadow: `0 3px 5px 2px rgba(97, 218, 251, .3)`,
    color: colors.dark,
    height: 48,
    padding: '0 30px',
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 10px 2px rgba(97, 218, 251, .4)`,
    },
  },

  // Section Styles
  section: {
    padding: '80px 0',
    [theme.breakpoints.down('md')]: {
      padding: '60px 0',
    },
  },
  sectionTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '3rem',
    color: colors.dark,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 60,
      height: 4,
      background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
      borderRadius: 2,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
    },
  },

  // Card Styles
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    boxShadow: `0 4px 20px rgba(97, 218, 251, 0.15)`,
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    border: `1px solid rgba(97, 218, 251, 0.1)`,
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: `0 12px 40px rgba(97, 218, 251, 0.25)`,
      border: `1px solid rgba(97, 218, 251, 0.3)`,
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: '24px',
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: colors.dark,
  },
  cardText: {
    color: colors.textLight,
    lineHeight: 1.6,
  },

  // Background Sections
  lightSection: {
    background: `linear-gradient(135deg, rgba(232, 244, 253, 0.6) 0%, rgba(209, 233, 251, 0.5) 100%)`,
    padding: '80px 0',
  },
  darkSection: {
    background: `linear-gradient(135deg, rgba(97, 218, 251, 0.3) 0%, rgba(74, 144, 226, 0.2) 50%, rgba(44, 62, 80, 0.1) 100%)`,
    padding: '80px 0',
    color: colors.white,
  },

  // Button Styles
  primaryButton: {
    background: `linear-gradient(45deg, ${colors.primary} 30%, ${colors.secondary} 90%)`,
    borderRadius: 16,
    padding: '12px 24px',
    textAlign: 'center',
    color: colors.dark,
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'all 0.3s ease',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      textDecoration: 'none',
      color: colors.dark,
      boxShadow: `0 8px 32px rgba(97, 218, 251, 0.4)`,
    },
  },

  // Container
  container: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 20px',
  },

  // Team Member Styles
  teamMember: {
    textAlign: 'center',
    marginBottom: 30,
  },
  teamAvatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    margin: '0 auto 16px',
    border: `3px solid ${colors.primary}`,
    boxShadow: `0 4px 20px rgba(97, 218, 251, 0.2)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      border: `3px solid ${colors.secondary}`,
    },
  },
  teamName: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: colors.dark,
    marginBottom: '8px',
  },
  teamPosition: {
    color: colors.textLight,
    fontSize: '0.9rem',
  },

  // Contact Styles
  contactCard: {
    background: colors.white,
    borderRadius: 16,
    padding: '30px',
    boxShadow: `0 8px 32px rgba(97, 218, 251, 0.15)`,
    transition: 'all 0.3s ease',
    height: '100%',
    border: `1px solid rgba(97, 218, 251, 0.1)`,
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: `0 16px 48px rgba(97, 218, 251, 0.25)`,
    },
  },
  contactIcon: {
    fontSize: '2.5rem',
    color: colors.primary,
    marginBottom: '16px',
  },

  // Form Styles
  formContainer: {
    background: colors.white,
    borderRadius: 16,
    padding: '40px',
    boxShadow: `0 8px 32px rgba(97, 218, 251, 0.15)`,
    border: `1px solid rgba(97, 218, 251, 0.1)`,
  },
  formField: {
    marginBottom: '20px',
  },
  formInput: {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid rgba(97, 218, 251, 0.3)`,
    borderRadius: 8,
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      border: `2px solid ${colors.primary}`,
      boxShadow: `0 0 0 3px rgba(97, 218, 251, 0.1)`,
    },
  },
  formTextarea: {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid rgba(97, 218, 251, 0.3)`,
    borderRadius: 8,
    fontSize: '1rem',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      border: `2px solid ${colors.primary}`,
      boxShadow: `0 0 0 3px rgba(97, 218, 251, 0.1)`,
    },
  },

  // Gallery Styles
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    padding: '20px 0',
  },
  galleryItem: {
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: `0 4px 20px rgba(97, 218, 251, 0.15)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: `0 12px 40px rgba(97, 218, 251, 0.25)`,
    },
  },
  galleryImage: {
    width: '100%',
    height: '250px',
    objectFit: 'cover',
  },

  // Map Container
  mapContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: `0 8px 32px rgba(97, 218, 251, 0.15)`,
    border: `1px solid rgba(97, 218, 251, 0.1)`,
  },

  // Responsive adjustments
  responsiveText: {
    [theme.breakpoints.down('md')]: {
      fontSize: '1.8rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
})); 